export default class OrderDecorator {
  constructor(orderData = {}, productData = {}) {
    this.data = {
      ordered_product_pricelist_items: [],
      ordered_product_order_fields: [],
      order_customer_participants: [],
      ...orderData
    }
    this.product = productData
    this.cacheProductAttributes()
  }

  get i18n_scope() {
    return `fe.NewOrderView.${this.data.app_config.kind}`
  }

  get i18n_params() {
    return {
      order_id: this.data.id,
      product_name: this.data.product_name,
      company_link: (
        !!(this.order && this.data.company) && (
          <a href={this.data.company.url}>{this.data.company.name}</a>
        )
      ) || "",
      company_approval_time_limit: this.data.company_approval_time_limit && I18n.t("base.x_hours", {
        count: this.data.company_approval_time_limit / 3600
      }) || "",
      confirmation_installment_st_name: this.confirmation_installment_st_name,
      confirmation_installment_amount_sum: this.data.confirmation_installment_amount_sum && st.formatMoney(this.data.confirmation_installment_amount_sum) || "",
      confirmation_installment_after_company_approval_time_limit: this.data.confirmation_installment_after_company_approval_time_limit && I18n.t("base.x_hours", {
        count: this.data.confirmation_installment_after_company_approval_time_limit / 3600
      }) || "",
      you_will_find_bank_wire_deposit_infos_in_order_confirmation: this.data.has_remaining_bank_wire_installment && I18n.t(`${this.i18n_scope}.you_will_find_bank_wire_deposit_infos_in_order_confirmation`) || "",
    }
  }

  get requiresCustomerRulesAgreement() {
    return !this.data.customer_rules_agreement;
  }

  get supplier_saas_url() {
    return this.data.company.saas_host && `${window.location.protocol}//${this.data.company.saas_host}${window.location.port ? `:${window.location.port}` : ''}`
  }

  get terms_url() {
    return this.data.terms_url
  }

  get new_review_url() {
    return `/reviews/new?company_id=${this.data.company.id}&order_id=${this.data.id}&order_token=${encodeURIComponent(this.data.token)}`
  }

  get still_building() {
    if (!this.data.status || this.data.status === "building") {
      return true;
    } else if (!this.data.manual && this.data.waiting_for_confirmation_online_payment && (this.data.confirmation_installment_before_company_approval || this.data.confirmation_installment_without_company_approval)) {
      return !this.data._hasPaymentFinished;
    } else {
      return false;
    }
  }

  get product_variant() {
    if (!this.product) { return; }
    if (this._product_variant && this._product_variant.id === this.data.product_variant_id) {
      return this._product_variant;
    }
    return this._product_variant = this.product.product_variants.find(pv => pv.id === this.data.product_variant_id);
  }

  get product_schedule_slot() {
    if (!this.product) { return }
    return this.data.product_schedule_slot
  }

  get product_quantity() {
    return _(this.data.ordered_product_pricelist_items)
      .map(ordered_item => ordered_item.quantity)
      .sum()
  }

  get persons_count() {
    return _(this.data.ordered_product_pricelist_items)
      .map(ordered_item => ordered_item.persons_count)
      .sum()
  }

  get days_count() {
    if (this.data.stock_end_date) {
      return moment(this.data.stock_end_date).diff(this.data.stock_start_date, "days") + 1;
    }
  }

  get nights_count() {
    if (this.days_count != null) {
      return this.days_count - 1;
    }
  }

  get marketplace_service_fee_tooltip() {
    return I18n.t("fe.new_order.marketplace_service_fee_tooltip");
  }

  get confirmation_installment_st_name() {
    if (this.data.requires_company_approval && !this.data.confirmation_installment_after_company_approval) {
      return I18n.t("fe.new_order.confirmation_installment_st_name.prepayment");
    } else {
      return I18n.t("fe.new_order.confirmation_installment_st_name.deposit");
    }
  }

  get v1_online_deposit_tooltip() {
    let company_approval_time_limit = (this.data.company_approval_time_limit || this.main_pricelist.company_approval_time_limit) / 3600;

    return `
    <p>${I18n.t(`fe.new_order.v1_online_deposit_tooltip.${this.data.app_config.kind}.line1`, {confirmation_installment_st_name: _.capitalize(this.confirmation_installment_st_name)})}</p>
    <p>${I18n.t(`fe.new_order.v1_online_deposit_tooltip.${this.data.app_config.kind}.line2`, {company_approval_time_limit})}</p>
    <p>${I18n.t(`fe.new_order.v1_online_deposit_tooltip.${this.data.app_config.kind}.line3`, {confirmation_installment_st_name: this.confirmation_installment_st_name})}</p>
    `;
  }

  get v1_bank_wire_deposit_tooltip() {
    return I18n.t("fe.new_order.v1_bank_wire_deposit_tooltip.${this.data.app_config.kind}");
  }

  get pdf_url() {
    if (this.data.id) {
      return `${st.GLOBALS.BACKEND_URL}zamowienia/${this.data.id}.pdf?t=${encodeURIComponent(this.data.token)}`;
    }
  }

  cacheProductAttributes() {
    if (this.product) {
      this.data.company_id = this.product.company_id;
      this.data.booking_type = this.product.booking_type;
      this.data.product_price_currency = this.product.price_currency;
      this.data.product_name = this.product.name;
      this.data.product_short_description = this.product.short_description;
      this.data.product_highlights = this.product.highlights;
      this.data.product_customer_hints_content = this.product.customer_hints_content;
    }

    if (this.product_variant) {
      this.data.product_variant_name = this.product.product_variants.length > 1 ? this.product_variant.name : null;
      this.data.product_variant_short_description = this.product_variant.short_description;
      this.data.product_included_items_content = this.product_variant.included_items_content;
      this.data.product_excluded_items_content = this.product_variant.excluded_items_content;
      this.data.product_customer_requirements_content = this.product_variant.customer_requirements_content;
      this.data.product_additional_pickup_instructions = this.product_variant.additional_pickup_instructions;
      this.data.requires_company_approval = this.product_variant.nb_requires_company_approval;
    }

    if (this.data.booking_type == "static_date_booking" && this.product_schedule_slot) {
      this.data.stock_start_date = this.product_schedule_slot.start_date;
      this.data.stock_end_date = this.product_schedule_slot.end_date;
      this.data.stock_starts_at = this.product_schedule_slot.starts_at;
      this.data.stock_ends_at = this.product_schedule_slot.ends_at;
      this.data.stock_includes_time = this.product_schedule_slot.includes_time;
      this.data.requires_company_approval = this.product_schedule_slot.availability_type === "available_with_company_approval";
    }
  }
};



// WEBPACK FOOTER //
// ./app/webpack/frontend/features/NewOrderView/lib/Order.js